import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { makeAutoObservable, runInAction } from "mobx";

import type { FullPatientsItem } from "@/features/patients/patients-tab-model";

import {
  apiGetAlertsMedicalWaitingRoom,
  apiGetAlertsTechnicalWaitingRoom,
  apiGetEvents,
  apiGetHfBasicMedDataPatientId,
  apiGetHfVitalsPatientId,
  apiGetMedicalReports,
  apiGetPatients,
  apiGetPatientsPatientIdComputed,
  apiGetPatientsPatientIdDerived,
  apiGetPatientsPatientIdEvents,
  apiGetPatientsPatientIdMonitoringInfo,
  apiGetPatientsPatientIdPpgs,
  apiGetSchedulesCompliancePatientId,
  apiGetSymptoms,
  type DefaultPatientAlertResponse,
  type DefaultPatientEventResponse,
  DerivedDataRollingType,
  EventCategory,
  PatientMonitoringType,
  PatientStatus,
  PermissionBit,
  PPGDerivedDataGroup,
  ReportType,
  UploadStatus,
} from "@/api";
import { hasMedicalAccess, hasPermission } from "@/constants/permissions";
import { mapWithConcurrency } from "@/utils/map-with-concurrency";
import { getServerNow } from "@/utils/server-time";
import { browserTimezone, type Timezone } from "@/utils/timezone";

import {
  dayFormatter,
  isCompletedToday,
  isResolvedToday,
  todayInTimezone,
  unseenAlertCount,
} from "./daily-monitoring-logic";
import {
  extractMetrics,
  type MetricDay,
  type PatientMetrics,
} from "./daily-monitoring-metrics";

const COMPLIANCE_CONCURRENCY = 6;
/** Max patients running per-row lazy fetches (metrics / ECG reports) at once. */
const ROW_FETCH_CONCURRENCY = 4;
/**
 * How far back the symptom fan-out looks. The symptom endpoint returns a
 * patient's whole history otherwise, and `extractMetrics` keeps only the shown
 * day's readings — so this just has to comfortably cover that day, whatever the
 * browser's timezone does to its boundaries.
 */
const SYMPTOM_LOOKBACK_DAYS = 2;

/** Lazily-fetched per-patient metric state, keyed by patient id in the model. */
export type MetricsEntry =
  | { status: "loading" }
  | { status: "loaded"; data: PatientMetrics }
  | { status: "error" };

/** A single openable ECG report file (medical report of type `ecg`). */
export type EcgReport = {
  uuid: string;
  /**
   * Upload time — what puts a report in a cell, what orders the cell, and what
   * the cell shows. Every report in a cell was uploaded on the day being
   * shown, so the date they share says nothing and is dropped here.
   */
  created_at: string;
};

/** Lazily-fetched per-patient ECG reports (see `ensureEcgReports`). */
export type EcgReportsEntry =
  | { status: "loading" }
  | { status: "loaded"; reports: EcgReport[] }
  | { status: "error" };

/**
 * Lazily-fetched date of a patient's most recent PPG (see `ensureLastMeasured`).
 * `null` once loaded means the patient has never measured.
 */
export type LastMeasuredEntry =
  | { status: "loading" }
  | { status: "loaded"; lastPpg: string | null }
  | { status: "error" };

/**
 * Lazily-fetched patient events, feeding the Trends panel's events timeline
 * (see `ensureEvents`).
 */
export type EventsEntry =
  | { status: "loading" }
  | { status: "loaded"; events: DefaultPatientEventResponse[] }
  | { status: "error" };

export type MonitoringRow = {
  /** Stable row key (also the DataTable `itemKeyField`). */
  patient_id: number;
  patient: FullPatientsItem;
  completed: boolean;
  resolved: boolean;
  /** Medical + technical alerts combined, as returned by the waiting rooms. */
  alerts: DefaultPatientAlertResponse[];
};

/**
 * A `MetricDay` whose timezone is still narrow enough for the compliance query
 * — `MetricDay` itself only needs a string to format against.
 */
type LoadedDay = MetricDay & { timezone: Timezone };

/** Today, in the browser's timezone — the day a `load` is a view of. */
function currentDay(): LoadedDay {
  const timezone = browserTimezone();
  return { iso: todayInTimezone(timezone), timezone };
}

/**
 * One metric endpoint's rows: fetched when the user's permissions allow it,
 * empty when they don't or when the call fails. A single failure degrades that
 * one metric to a dash instead of sinking the row.
 */
function allowed<T>(
  permitted: boolean,
  fetch: () => Promise<T[]>,
): Promise<T[]> {
  if (!permitted) return Promise.resolve([]);
  return fetch().catch(() => []);
}

/** Start of the symptom lookback window, as an ISO datetime. */
function symptomLookbackFrom(): string {
  const from = new Date(getServerNow());
  from.setDate(from.getDate() - SYMPTOM_LOOKBACK_DAYS);
  return from.toISOString();
}

export class DailyMonitoringModel {
  loading = false;
  error: string | null = null;
  rows: MonitoringRow[] = [];
  /**
   * Patient whose Trends panel is open in the side sheet, or null. One at a
   * time, unlike the set of inline expansions this replaced — the sheet shows
   * a single patient's charts and closing it is the only way back to the
   * worklist.
   */
  reportPatientId: number | null = null;
  /** Lazily-loaded metric columns, keyed by patient id (see `ensureMetrics`). */
  metrics = new Map<number, MetricsEntry>();
  /** Lazily-loaded ECG reports, keyed by patient id (see `ensureEcgReports`). */
  ecgReports = new Map<number, EcgReportsEntry>();
  /** Lazily-loaded last-measurement dates (see `ensureLastMeasured`). */
  lastMeasured = new Map<number, LastMeasuredEntry>();
  /** Lazily-loaded patient events (see `ensureEvents`). */
  events = new Map<number, EventsEntry>();
  /**
   * The day this worklist is a view of, fixed by `load`. Everything on the page
   * has to describe the *same* day: the Completed / Not-completed split is
   * decided against it, so the metric columns must be extracted against it too.
   * Recomputing "today" per lazy metric fetch would let a tab open across
   * midnight show rows classified as completed yesterday with today's (empty)
   * readings — and, since metrics load per visible page, could even extract two
   * rows of one table against different days. A stale tab is fixed by
   * reloading the worklist, not by moving half of it forward.
   */
  private day: LoadedDay = currentDay();

  private metricsInFlight = 0;
  private metricsQueue: (() => void)[] = [];

  /**
   * `permissions` gates the fetches, mirroring what the route's columns show:
   * the page is reachable on GROUP_MONITORING_ROUTERS alone, but each of these
   * endpoints wants a bit of its own, so a user missing one would otherwise
   * spend a 403 per row on data they can't be shown. See `dmPermissions`.
   */
  constructor(
    private readonly assignedOnly: boolean,
    private readonly permissions: PermissionBit[],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private can(bit: PermissionBit) {
    return hasPermission(this.permissions, bit);
  }

  private get canMedical() {
    return hasMedicalAccess(this.permissions);
  }

  /**
   * Opens the Trends sheet for a patient. Open-only, not a toggle: the sheet is
   * modal, so its overlay covers the row action that opened it — closing goes
   * through the sheet's own control.
   */
  openReport(patientId: number) {
    this.reportPatientId = patientId;
  }

  closeReport() {
    this.reportPatientId = null;
  }

  get completedRows() {
    return this.rows.filter((r) => r.completed);
  }

  get notCompletedRows() {
    return this.rows.filter((r) => !r.completed);
  }

  get summary() {
    const completed = this.completedRows;
    return {
      total: this.rows.length,
      completed: completed.length,
      reviewed: completed.filter((r) => r.resolved).length,
      // Unseen, not total: the Alerts column badge counts every alert in its
      // popover, so this is labelled "With unseen alerts" to keep the two
      // numbers from reading as the same thing.
      withUnseenAlerts: this.rows.filter((r) => unseenAlertCount(r.alerts) > 0)
        .length,
      completionPct: this.rows.length
        ? Math.round((completed.length / this.rows.length) * 100)
        : 0,
    };
  }

  markResolvedLocally(patientId: number) {
    const row = this.rows.find((r) => r.patient_id === patientId);
    if (row) row.resolved = true;
  }

  /**
   * Fetch a patient's latest metric values once, on demand. Called by each
   * metric cell on mount; idempotent per patient, so the row's cells collapse
   * to a single fetch. Because the table paginates, only the visible page's
   * patients ever fetch — this is the "lazy per-row" behaviour that keeps the
   * six-endpoint fan-out bounded. A capped gate limits how many patients load
   * concurrently, and any endpoint failure degrades that metric to a dash
   * rather than sinking the row.
   */
  async ensureMetrics(patientId: number) {
    if (this.metrics.has(patientId)) return;
    this.metrics.set(patientId, { status: "loading" });
    // Endpoints the user's permissions don't reach are skipped rather than
    // requested and caught: `extractMetrics` reads the empty array as "no
    // reading", which is what their (hidden) column would have shown anyway.
    const medical = this.canMedical;
    const ppgMeta = this.can(PermissionBit.PPG_META);
    // LVFP is the computed response's only consumer, and its column is dropped
    // without ACCESS_LVFP — so without the bit this request is one per row
    // whose response is thrown away. Same pair the column is built on.
    const lvfp = medical && this.can(PermissionBit.ACCESS_LVFP);
    await this.withRowFetchGate(async () => {
      try {
        const [computed, derived, vitals, basics, symptoms, ppgs] =
          await Promise.all([
            allowed(lvfp, () =>
              apiGetPatientsPatientIdComputed({
                path: { patient_id: patientId },
                // LVFP is the only computed metric this page renders; narrowing
                // server-side keeps the response from carrying every other
                // computed type. Same glob the LVFP table uses.
                query: {
                  search_string: "left*ventricular*filling*pressure",
                  paginate: false,
                },
              }).then((r) => r.data.computed_data),
            ),
            allowed(medical, () =>
              apiGetPatientsPatientIdDerived({
                path: { patient_id: patientId },
                query: {
                  data_groups: [
                    PPGDerivedDataGroup.hr,
                    PPGDerivedDataGroup.spo2,
                  ],
                  rolling_type: [DerivedDataRollingType.full],
                  // Only the mean is displayed; the other aggregations in these
                  // groups (std / min / max / ...) are dead weight in the
                  // response. `extractMetrics` still selects the exact
                  // measurement_type — this just trims what we download.
                  search_string: "*mean*",
                  paginate: false,
                },
              }).then((r) => r.data.derived_data),
            ),
            allowed(medical, () =>
              apiGetHfVitalsPatientId({
                path: { patient_id: patientId },
              }).then((r) => r.data.medical_data),
            ),
            allowed(medical, () =>
              apiGetHfBasicMedDataPatientId({
                path: { patient_id: patientId },
              }).then((r) => r.data.medical_data),
            ),
            allowed(medical, () =>
              apiGetSymptoms({
                query: {
                  patient_ids: [patientId],
                  datetime_from: symptomLookbackFrom(),
                },
              }).then((r) => r.data.symptoms),
            ),
            allowed(ppgMeta, () =>
              apiGetPatientsPatientIdPpgs({
                path: { patient_id: patientId },
              }).then((r) => r.data.ppg_data),
            ),
          ]);
        const data = extractMetrics(
          { computed, derived, vitals, basics, symptoms, ppgs },
          this.day,
        );
        runInAction(() =>
          this.metrics.set(patientId, { status: "loaded", data }),
        );
      } catch {
        runInAction(() => this.metrics.set(patientId, { status: "error" }));
      }
    });
  }

  /**
   * Fetch a patient's ECG report files once, on demand (medical reports of type
   * `ecg` that finished uploading, dated the day this worklist is a view of).
   * Same lazy-per-row, gated pattern as `ensureMetrics`; a failure degrades the
   * cell to a dash.
   *
   * Today-only, like every other column here: the page answers "what came in
   * today", so an ECG uploaded on an earlier day is not this row's business —
   * the patient's full history is a click away on their profile.
   */
  async ensureEcgReports(patientId: number) {
    if (this.ecgReports.has(patientId)) return;
    if (!this.canMedical) {
      this.ecgReports.set(patientId, { status: "loaded", reports: [] });
      return;
    }
    this.ecgReports.set(patientId, { status: "loading" });
    await this.withRowFetchGate(async () => {
      try {
        const res = await apiGetMedicalReports({
          // Deliberately unfiltered server-side: `search_string` on this
          // endpoint matches against `report_type`, which is an integer
          // column, so the API's ILIKE against it raises and surfaces as a
          // 503 for *any* value (Seerlinq/db-api, never worked). The
          // client-side filter below is what selects ECGs anyway — it also
          // applies the upload-status rule — so dropping the param only costs
          // a patient's other reports in the response.
          query: { patient_ids: [patientId] },
        });
        // Uploaded today, not *dated* today: `report_date` is entered by hand
        // in the add-report dialog, so an ECG recorded yesterday and uploaded
        // this morning — the normal way that field gets filled — carries
        // yesterday's date while being exactly what this row is waiting on.
        // `created_at` is also what the cell orders and labels by, so one
        // field answers both.
        const fmt = dayFormatter(this.day.timezone);
        const uploadedOnDay = (createdAt: string) => {
          const t = Date.parse(createdAt);
          return !Number.isNaN(t) && fmt.format(t) === this.day.iso;
        };
        const reports: EcgReport[] = res.data.medical_reports
          .filter(
            (r) =>
              r.report_type === ReportType.ecg &&
              r.upload_status === UploadStatus.success &&
              uploadedOnDay(r.created_at),
          )
          .map((r) => ({ uuid: r.uuid, created_at: r.created_at }));
        runInAction(() =>
          this.ecgReports.set(patientId, { status: "loaded", reports }),
        );
      } catch {
        runInAction(() => this.ecgReports.set(patientId, { status: "error" }));
      }
    });
  }

  /**
   * Fetch the date a patient last measured, once, on demand. Same lazy-per-row,
   * gated pattern as `ensureMetrics`; a failure degrades the cell to a dash.
   *
   * Only the Not-completed table reads this — it is the column that says how
   * stale a missing measurement is, which is the whole triage question there.
   * `monitoring-info` carries `last_ppg` directly, so this stays one call per
   * patient rather than paging that patient's PPG history to find its newest.
   */
  async ensureLastMeasured(patientId: number) {
    if (this.lastMeasured.has(patientId)) return;
    if (!this.can(PermissionBit.RUN_HEART_CORE_QUICKLOOK)) {
      this.lastMeasured.set(patientId, { status: "loaded", lastPpg: null });
      return;
    }
    this.lastMeasured.set(patientId, { status: "loading" });
    await this.withRowFetchGate(async () => {
      try {
        const res = await apiGetPatientsPatientIdMonitoringInfo({
          path: { patient_id: patientId },
        });
        const lastPpg = res.data.last_ppg;
        runInAction(() =>
          this.lastMeasured.set(patientId, { status: "loaded", lastPpg }),
        );
      } catch {
        runInAction(() =>
          this.lastMeasured.set(patientId, { status: "error" }),
        );
      }
    });
  }

  /**
   * Fetch a patient's events once, on demand — the timeline above the Trends
   * panel's charts, the same one the profile's HeartCore tab shows. Only the
   * open sheet reads this, so it is fetched when the sheet mounts rather than
   * per row. A failure costs the timeline, not the charts.
   */
  async ensureEvents(patientId: number) {
    // Every state but `error` short-circuits: the sheet is reopenable, so a
    // transient /events outage must not cost the timeline for the rest of the
    // model's life the way a plain `has` check would.
    const cached = this.events.get(patientId);
    if (cached && cached.status !== "error") return;
    if (!this.can(PermissionBit.VIEW_EVENTS)) {
      this.events.set(patientId, { status: "loaded", events: [] });
      return;
    }
    this.events.set(patientId, { status: "loading" });
    try {
      const res = await apiGetPatientsPatientIdEvents({
        path: { patient_id: patientId },
      });
      runInAction(() =>
        this.events.set(patientId, {
          status: "loaded",
          events: res.data.events,
        }),
      );
    } catch {
      runInAction(() => this.events.set(patientId, { status: "error" }));
    }
  }

  private withRowFetchGate<T>(fn: () => Promise<T>): Promise<T> {
    const run = async () => {
      this.metricsInFlight++;
      try {
        return await fn();
      } finally {
        this.metricsInFlight--;
        this.metricsQueue.shift()?.();
      }
    };
    if (this.metricsInFlight < ROW_FETCH_CONCURRENCY) return run();
    return new Promise<T>((resolve, reject) => {
      this.metricsQueue.push(() => run().then(resolve, reject));
    });
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // Fixed here and read by everything the load produces — see `day`.
      this.day = currentDay();
      const { timezone, iso: today } = this.day;

      const patientsRes = await apiGetPatients({
        query: {
          patient_status: [PatientStatus.active],
          monitoring_type: [PatientMonitoringType.self_measurement],
          load_type: "basic",
          paginate: false,
          ...(this.assignedOnly ? { assigned_only: true } : {}),
        },
      });
      const patients = patientsRes.data.patients as FullPatientsItem[];

      // Fan out compliance (capped concurrency). A failed call for one
      // patient must not sink the whole batch, so it resolves to `null`
      // and that patient is treated as not completed today.
      const compliance = await mapWithConcurrency(
        patients,
        COMPLIANCE_CONCURRENCY,
        (p) =>
          apiGetSchedulesCompliancePatientId({
            path: { patient_id: p.patient_id },
            query: { timezone, date_from: today, date_to: today },
          })
            .then((r) => r.data)
            .catch(() => null),
      );

      const rows: MonitoringRow[] = patients.map((patient, i) => ({
        patient_id: patient.patient_id,
        patient,
        completed: compliance[i]
          ? isCompletedToday(compliance[i], today)
          : false,
        resolved: false,
        alerts: [],
      }));

      // Batch events for completed patients → resolved flag. Enrichment, not
      // classification: if it fails, every completed row simply stays
      // unresolved rather than the whole worklist collapsing to an error.
      // Skipped without VIEW_EVENTS, which is also what hides the Resolve
      // column — there is no review state to read or write.
      const completedIds = rows
        .filter((r) => r.completed)
        .map((r) => r.patient_id);
      if (completedIds.length && this.can(PermissionBit.VIEW_EVENTS)) {
        const events = await apiGetEvents({
          query: {
            patient_ids: completedIds,
            event_categories: [EventCategory.physician_notified],
          },
        })
          .then((r) => r.data.events)
          .catch(() => []);
        const byPatient = new Map<number, typeof events>();
        for (const e of events) {
          const list = byPatient.get(e.patient_id) ?? [];
          list.push(e);
          byPatient.set(e.patient_id, list);
        }
        for (const row of rows) {
          if (row.completed) {
            row.resolved = isResolvedToday(
              byPatient.get(row.patient_id) ?? [],
              today,
            );
          }
        }
      }

      // Alerts: two bucketed calls, indexed by patient. Also enrichment — a
      // waiting room being down costs the Alerts column, not the worklist.
      // Skipped without VIEW_WAITING_ROOM, which is also what hides that
      // column and the "With unseen alerts" summary.
      const canWaitingRoom = this.can(PermissionBit.VIEW_WAITING_ROOM);
      const waitingRoomQuery = this.assignedOnly
        ? { query: { assigned_only: true } }
        : {};
      const [med, tech] = await Promise.all([
        allowed(canWaitingRoom, () =>
          apiGetAlertsMedicalWaitingRoom(waitingRoomQuery).then(
            (r) => r.data.waiting_room,
          ),
        ),
        allowed(canWaitingRoom, () =>
          apiGetAlertsTechnicalWaitingRoom(waitingRoomQuery).then(
            (r) => r.data.waiting_room,
          ),
        ),
      ]);
      const medByPatient = new Map(med.map((w) => [w.patient_id, w]));
      const techByPatient = new Map(tech.map((w) => [w.patient_id, w]));
      for (const row of rows) {
        const m = medByPatient.get(row.patient_id);
        const t = techByPatient.get(row.patient_id);
        row.alerts = [
          ...(m
            ? [
                ...m.lvfp_alerts,
                ...m.vitals_alerts,
                ...m.symptom_alerts,
                ...m.ppg_quality_alerts,
                ...m.compliance_alerts,
              ]
            : []),
          ...(t
            ? [
                ...t.ppg_quality_alerts,
                ...t.other_technical_alerts,
                ...t.compliance_alerts,
              ]
            : []),
        ];
      }

      runInAction(() => {
        this.rows = rows;
        this.loading = false;
      });
    } catch (e) {
      runInAction(() => {
        this.error =
          e instanceof Error ? e.message : i18n._(msg`Failed to load`);
        this.loading = false;
      });
    }
  }
}
